import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useMutation, type QueryClient } from '@tanstack/react-query';
import type {
  ParsedCsvRow,
  LookupRow,
  LookupTabData,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { CSV_FILE_CONFIGS } from '~/app/constants/csvAllowedFields';
import { ComponentSchemaService } from '~/app/services/knowledgeBase';
import { nextKey } from './csvUtils';
import {
  parseStringList,
  serializeStringList,
} from '~/app/hooks/knowledgeBase/stringListUtils';

// ── Hook ────────────────────────────────────────────────────────────

export const useCrudOperations = (
  queryClient: QueryClient,
  lookups: Record<string, LookupTabData>,
  onRefresh?: (configName: string) => void,
) => {
  const { t } = useTranslation('componentSchema');

  const [editingRows, setEditingRows] = useState<
    Record<string, Record<string, ParsedCsvRow>>
  >({});

  const saveRowMutation = useMutation({
    mutationFn: (payload: {
      configName: string;
      key: string;
      editData: ParsedCsvRow;
    }) =>
      ComponentSchemaService.saveRow(
        payload.configName,
        payload.key,
        payload.editData,
      ),
    onSuccess: (_data, variables) => {
      if (onRefresh) {
        onRefresh(variables.configName);
      } else {
        void queryClient.invalidateQueries({
          queryKey: ['lookup', variables.configName],
        });
      }
    },
  });

  const deleteRowMutation = useMutation({
    mutationFn: (payload: { configName: string; key: string }) =>
      ComponentSchemaService.deleteRow(payload.configName, payload.key),
    onSuccess: (_data, variables) => {
      if (onRefresh) {
        onRefresh(variables.configName);
      } else {
        void queryClient.invalidateQueries({
          queryKey: ['lookup', variables.configName],
        });
      }
    },
  });

  // ── Handlers ────────────────────────────────────────────────────

  const handleAddRow = useCallback((configName: string) => {
    const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
    if (!config) return;

    const key = nextKey();
    const emptyRow: LookupRow = { _key: key };
    config.columns.forEach((col) => {
      emptyRow[col.key] = col.fieldType === 'stringArray' ? [] : '';
    });

    const editData: ParsedCsvRow = {};
    config.columns.forEach((col) => {
      editData[col.key] = col.fieldType === 'stringArray' ? '[]' : '';
    });
    setEditingRows((prev) => ({
      ...prev,
      [configName]: { ...(prev[configName] || {}), [key]: editData },
    }));
  }, []);

  const handleEditRow = useCallback(
    (configName: string, key: string, row: ParsedCsvRow) => {
      setEditingRows((prev) => ({
        ...prev,
        [configName]: { ...(prev[configName] || {}), [key]: row },
      }));
    },
    [],
  );

  const handleDeleteRow = useCallback(
    async (configName: string, key: string) => {
      try {
        if (!key.startsWith('row_')) {
          await deleteRowMutation.mutateAsync({ configName, key });
        }

        setEditingRows((prev) => {
          const next = { ...prev };
          if (next[configName]) {
            const { [key]: _, ...rest } = next[configName];
            next[configName] = rest;
          }
          return next;
        });

        message.success(t('messages.rowDeleted'));
      } catch {
        message.error(t('messages.deleteRowFailed'));
      }
    },
    [deleteRowMutation, t],
  );

  const handleSaveRow = useCallback(
    async (configName: string, key: string) => {
      const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
      const editData = editingRows[configName]?.[key];
      if (!config || !editData) return;

      // Validate required fields (skip derived / non-CSV columns)
      for (const col of config.columns) {
        if (col.excludeFromCsv) continue;
        if (col.fieldType === 'stringArray') {
          if (col.required && parseStringList(editData[col.key]).length === 0) {
            message.warning(t('messages.fieldRequired', { field: col.title }));
            return;
          }
          continue;
        }
        if (
          col.required &&
          (!editData[col.key] || editData[col.key].trim() === '')
        ) {
          message.warning(t('messages.fieldRequired', { field: col.title }));
          return;
        }
      }

      // Validate regex patterns
      for (const col of config.columns) {
        if (col.excludeFromCsv) continue;
        if (col.fieldType === 'stringArray') continue;
        const value = editData[col.key];
        if (col.regex && value && value.trim() !== '') {
          const regex = new RegExp(col.regex);
          if (!regex.test(value.trim())) {
            message.warning(
              col.regexMessage ??
                t('messages.fieldInvalid', { field: col.title }),
            );
            return;
          }
        }
      }

      try {
        await saveRowMutation.mutateAsync({ configName, key, editData });

        setEditingRows((prev) => {
          const next = { ...prev };
          if (next[configName]) {
            const { [key]: _, ...rest } = next[configName];
            next[configName] = rest;
          }
          return next;
        });

        message.success(t('messages.rowSaved'));
      } catch {
        message.error(t('messages.saveRowFailed'));
      }
    },
    [editingRows, saveRowMutation, t],
  );

  const handleCancelEdit = useCallback(
    (configName: string, key: string) => {
      const _row = lookups[configName]?.rows.find((r) => r._key === key);

      setEditingRows((prev) => {
        const next = { ...prev };
        if (next[configName]) {
          const { [key]: _, ...rest } = next[configName];
          next[configName] = rest;
        }
        return next;
      });
    },
    [lookups],
  );

  const handleApplySynonyms = useCallback(
    async (configName: string, key: string, synonyms: string[]) => {
      const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
      if (!config) return;

      const serialized = serializeStringList(synonyms);
      const existingEdit = editingRows[configName]?.[key];

      if (existingEdit) {
        setEditingRows((prev) => ({
          ...prev,
          [configName]: {
            ...(prev[configName] || {}),
            [key]: {
              ...existingEdit,
              synonyms: serialized,
            },
          },
        }));
        return;
      }

      const row = lookups[configName]?.rows.find((r) => r._key === key);
      if (!row) return;

      const editData: ParsedCsvRow = {};
      config.columns.forEach((col) => {
        if (col.excludeFromCsv) return;
        if (col.key === 'synonyms' || col.fieldType === 'stringArray') {
          editData[col.key] = serialized;
          return;
        }
        editData[col.key] = String(row[col.key] ?? '');
      });

      try {
        await saveRowMutation.mutateAsync({ configName, key, editData });
        message.success(t('messages.rowSaved'));
      } catch {
        message.error(t('messages.saveRowFailed'));
        throw new Error('Failed to save synonyms');
      }
    },
    [editingRows, lookups, saveRowMutation, t],
  );

  return {
    editingRows,
    onAddRow: handleAddRow,
    onEditRow: handleEditRow,
    onDeleteRow: handleDeleteRow,
    onSaveRow: handleSaveRow,
    onCancelEdit: handleCancelEdit,
    onApplySynonyms: handleApplySynonyms,
  };
};
